import { Button, Counter, Flex, Motion, Text, Card } from "../../components";
import { HeartOutlined, HeartFilled } from "@ant-design/icons";
import { previewLineTotal } from "../../utils/menu";
import { useCartStore } from "../../stores";
import { Item } from "../../types/item";
import { useApp, useMenu } from "../../hooks";
import { Tools } from "../../utils";
import Remove from "./remove";

const FavoriteButton = ({
  favorite = false,
  onClick = () => {},
}: {
  favorite?: boolean;
  onClick?: () => void;
}) => (
  <Motion
    as="button"
    type="button"
    aria-label={favorite ? "Remove from favorites" : "Save to favorites"}
    className="absolute right-[1rem] top-[1rem] z-[2] flex h-[3.4rem] w-[3.4rem] cursor-pointer items-center justify-center rounded-full border-0 bg-white text-[1.8rem] shadow-card"
    style={{
      color: favorite ? "var(--color-primary)" : "var(--color-secondary)",
    }}
    whileTap={{ scale: 0.88 }}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    <Motion as="span" name="pop" key={favorite ? "on" : "off"} className="flex">
      {favorite ? <HeartFilled /> : <HeartOutlined />}
    </Motion>
  </Motion>
);

interface Props extends Item {
  onFavorite?: () => void;
}

const MenuItemCard = ({ onFavorite = () => {}, ...item }: Props) => {
  const cartStore = useCartStore();
  const { modal, isSiteActive, message } = useApp();
  const { openItem } = useMenu();
  const unavailable = !(item.isAvailable ?? true) || !isSiteActive;

  const cartItem = cartStore.items.filter((_) => _.uuid === item.uuid);
  const quantity = Tools.sum(cartItem.map((_) => _.quantity));
  const displayPrice = item.hasVariations
    ? previewLineTotal({ ...item, price: 0 }, 1) || item.totalPrice
    : item.price;

  const onAdd = (e?: any) => {
    e?.stopPropagation?.();
    if (unavailable) {
      message.error(
        !isSiteActive
          ? "We are temporarily closed and not accepting orders."
          : `${item.name} is currently unavailable`,
      );
      return;
    }
    !!item.options?.length
      ? onClick()
      : cartStore.add(item).catch((error: any) => {
          message.error(error.message || "Could not update cart");
        });
  };

  const onRemove = async (e?: any) => {
    e?.stopPropagation?.();
    if (cartItem.length <= 1) await cartStore.remove(cartItem[0]);
    else {
      const removeModal = modal.show({
        title: item.name,
        classNames: { body: "p-0" },
        content: <Remove {...item} close={() => removeModal.destroy()} />,
      });
    }
  };

  const onClick = () => {
    if (unavailable) return;
    openItem(item.uuid);
  };

  return (
    <Card
      onClick={unavailable ? undefined : onClick}
      classNames={{ body: "h-100 p-0" }}
      className="h-full cursor-pointer overflow-hidden transition duration-200 hover:shadow-card active:shadow-card"
      style={{
        opacity: unavailable ? 0.55 : 1,
        cursor: unavailable ? "default" : "pointer",
      }}
    >
      <Flex vertical className="relative h-full">
        <FavoriteButton favorite={!!item.favorite} onClick={onFavorite} />
        {!!item.imageUrl && (
          <div className="item-card-photo relative w-full shrink-0 overflow-hidden bg-primary-soft">
            <img
              alt={item.name}
              className="absolute inset-0 h-full w-full object-cover"
              src={Tools.mediaUrl(item.imageUrl)}
            />
          </div>
        )}
        <Flex vertical className="flex-1 p-15 mobile:p-8">
          <Text
            weight={600}
            level={4}
            className={item.imageUrl ? "" : "pr-[3.2rem]"}
            children={item.name}
          />
          {!!item.desc && (
            <Text type="secondary" level="text" children={item.desc} />
          )}
          {unavailable && (
            <Text type="error" className="m-5-t" children="Unavailable" />
          )}
          <div className="mt-auto">
            <Flex gap={"1rem"} align="center" className="m-20-t">
              <Text
                level={4}
                weight={"900"}
                children={`${item.hasVariations ? "from " : ""}${Tools.toCurrency(
                  displayPrice,
                )}`}
              />
              {!!item.previousPrice && (
                <Text
                  level={5}
                  type="secondary"
                  style={{ textDecoration: "line-through" }}
                  children={Tools.toCurrency(item.previousPrice)}
                />
              )}
            </Flex>
            <div className="m-10-t" onClick={(e) => e.stopPropagation()}>
              {!quantity ? (
                <Button
                  block
                  title="Add"
                  disabled={unavailable}
                  onClick={onAdd}
                  style={{ width: "100%", borderRadius: 999 }}
                />
              ) : (
                <Counter
                  onAdd={onAdd}
                  onRemove={onRemove}
                  value={quantity}
                  className="width-100% justify-between bg-accent-soft rounded-full"
                />
              )}
            </div>
          </div>
        </Flex>
      </Flex>
    </Card>
  );
};

export default MenuItemCard;
